import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';

import { useWebSocket } from './WebSocketProvider';
import { DataState } from './DataProvider';
import { fetchExecutionBotSessions } from '../services/api';
const BotSessionsContext = createContext();

export const BotSessionsProvider = ({ children }) => {
  const { subscribe, isConnected } = useWebSocket();
  const { bots, userGoogleId } = DataState();
  const [botSessions, setBotSessions] = useState([]);

  // Create a map of bot_id to botTitle using the bots data.
  const botMap = bots.reduce((acc, { id, name }) => {
    acc[id] = name;
    return acc;
  }, {});

  // This function handles incoming notifications.
  const handleNewBotStatusNotification = (notification) => {
    if (notification.bot_id && notification.bot_id in botMap) {
      setBotSessions((prevSessions) => {
        // Copy the existing sessions array.
        const sessions = [...prevSessions];
        // Find if the session already exists.
        const sessionIndex = sessions.findIndex(
          (s) => s.session_id === notification.session_id,
        );

        if (sessionIndex !== -1) {
          // Update the status if the session exists.
          sessions[sessionIndex] = {
            ...sessions[sessionIndex],
            status: notification.status,
          };
        } else {
          // Add the new session to the front.
          sessions.unshift({
            bot_id: notification.bot_id,
            status: notification.status,
            session_id: notification.session_id,
            botTitle: botMap[notification.bot_id],
          });
        }

        // If the list grows beyond 10 sessions, remove the last session with status "success".
        if (sessions.length > 15) {
          for (let i = sessions.length - 1; i >= 0; i--) {
            if (sessions[i].status !== 'running') {
              sessions.splice(i, 1);
              break; // Remove only one session.
            }
          }
        }
        if (notification.status === 'running') {
          toast.success(
            `Workflow Execution Started: ${botMap[notification.bot_id]}`,
          );
        } else if (notification.status === 'failed') {
          toast.error(
            `Workflow Execution Failed: ${botMap[notification.bot_id]}`,
          );
        } else if (notification.status === 'success') {
          toast.success(
            `Workflow Execution Complete: ${botMap[notification.bot_id]}`,
          );
        }
        return sessions;
      });
    }
  };

  useEffect(() => {
    if (!isConnected) return;
    const eventKey = 'bot_session_status_notification';

    // Subscribe to the WebSocket event.
    const unsubscribe = subscribe({
      [eventKey]: (data) => {
        // toast.success(JSON.stringify(data));
        handleNewBotStatusNotification(data);
      },
    });

    // Cleanup on unmount or dependency change.
    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line
  }, [subscribe, isConnected, bots]);

  useEffect(() => {
    const setInitialBotSessions = async () => {
      try {
        const executionSessions = await fetchExecutionBotSessions(userGoogleId);
        // Handle null, undefined, or empty array response
        if (!executionSessions || executionSessions.length === 0) {
          setBotSessions([]);
          toast.error('No sessions found.');
          return;
        }

        setBotSessions(
          executionSessions.map((session) => ({
            ...session,
            botTitle: session.name,
          })),
        );
      } catch (error) {
        toast.error(error);
      }
    };
    if (bots && bots.length > 0) {
      setInitialBotSessions();
    }
    // eslint-disable-next-line
  }, [bots]);

  return (
    <BotSessionsContext.Provider value={{ botSessions }}>
      {children}
    </BotSessionsContext.Provider>
  );
};

// Custom hook for easy consumption.
export const useBotSessions = () => useContext(BotSessionsContext);
